import type { FormItemProps } from 'antd';
import { Form } from 'antd';
import type { DefaultOptionType } from 'antd/es/select';
import Select from 'antd/es/select';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { fetchPlacePredictions } from 'app/utils/googlePlace/googlePlace';
import type {
  AllPlacesTypes,
  AutocompletePrediction,
} from 'app/utils/googlePlace/typings';
import { useDebounce } from 'app/utils/hooks/useDebounce';

type Props = {
  minCharacters?: number;
  onSelectPlaceId?: (placeId: string) => void;
  placePredictionTypes: AllPlacesTypes[];
  formItemProps?: FormItemProps;
  initialValue?: string;
  renderLocationLabel: (location: Location) => React.ReactNode;
  placeholder?: string;
};

export const LocationSelect = ({
  onSelectPlaceId,
  minCharacters,
  placePredictionTypes,
  formItemProps,
  initialValue,
  placeholder,
  renderLocationLabel,
}: Props) => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchValue = useDebounce(searchValue, 200);

  const setPlacePredictionsLocations = (
    predictions: AutocompletePrediction[]
  ): void => {
    if (!predictions) {
      return;
    }

    const formattedPredictions = predictions
      .map((pred) => ({
        id: pred.place_id,
        text: pred.structured_formatting.main_text,
        subtext: pred.structured_formatting.secondary_text,
      }))
      .filter((d) => d.id);

    setLocations(formattedPredictions);
  };

  const findPlaces = useCallback(
    (value: string) => {
      if ((minCharacters && value.length < minCharacters) || !value.length) {
        setLocations([]);

        return;
      }

      try {
        fetchPlacePredictions(
          {
            input: value,
            types: placePredictionTypes,
          },
          setPlacePredictionsLocations
        );
      } catch (e) {
        console.error('Error while fetching google maps locations: ', e);
      }
    },
    [minCharacters, placePredictionTypes]
  );

  useEffect(() => {
    findPlaces(debouncedSearchValue);
  }, [debouncedSearchValue, findPlaces]);

  useEffect(() => {
    if (!!initialValue) {
      setSearchValue(initialValue);
    }
  }, [initialValue]);

  const options: DefaultOptionType[] = useMemo(
    () =>
      locations.map((location) => ({
        value: location.id,
        label: renderLocationLabel(location),
      })),
    [locations, renderLocationLabel]
  );

  return (
    <Form.Item {...formItemProps}>
      <Select
        showSearch
        placeholder={placeholder}
        showArrow={false}
        filterOption={false}
        onSearch={setSearchValue}
        onChange={onSelectPlaceId}
        options={options}
        notFoundContent={
          minCharacters
            ? `No results. Please type at least ${minCharacters} characters.`
            : 'No results'
        }
      />
    </Form.Item>
  );
};

type Location = {
  id: number;
  text: string;
  subtext: string;
};
